// src/features/Customers/CustomerDetail.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { licenseService } from '../../apiService';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { FleetStatusStrip } from './FleetStatusStrip';
import ActivationLabelExport from '../../ActivationLabelExport';
import { countByStage, stageLabel, stageBadgeVariant } from '../../lib/onboardingDisplay';

// Fetch every page of one customer's licenses (customer-scoped → bounded).
async function fetchAllForCustomer(customerNumber, token) {
  let all = [];
  let nextToken = null;
  do {
    const params = { customerNumber, pageSize: '100' };
    if (nextToken) params.nextToken = nextToken;
    // eslint-disable-next-line no-await-in-loop
    const res = await licenseService.listLicenses(params, token);
    all = all.concat(res.data || []);
    nextToken = res.nextToken || null;
  } while (nextToken);
  return all;
}

export default function CustomerDetail() {
  const { customerNumber } = useParams();
  const auth = useAuth();
  const token = auth.user?.access_token;
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStage, setActiveStage] = useState(null);
  const [selected, setSelected] = useState({});
  const [showProvision, setShowProvision] = useState(false);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(() => {
    if (!token) { setLoading(false); return Promise.resolve(); }
    setLoading(true);
    setError(null);
    return fetchAllForCustomer(customerNumber, token)
      .then((rows) => { setLicenses(rows); setLoading(false); })
      .catch((e) => { setError(e.message || 'Failed to load fleet'); setLoading(false); });
  }, [customerNumber, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p>Loading fleet…</p>;
  if (error) return <p role="alert">{error}</p>;

  const counts = countByStage(licenses);
  const visible = activeStage ? licenses.filter((l) => l.onboardingStage === activeStage) : licenses;
  const selectedRows = licenses.filter((l) => selected[l.serialNumber]);
  // Only provisioned devices can ship — the backend 409s on notProvisioned. Gate the action client-side too.
  const shippableRows = selectedRows.filter((l) => l.onboardingStage !== 'notProvisioned');
  const customerName = licenses[0]?.customerName || customerNumber;

  const toggle = (sn) => setSelected((p) => ({ ...p, [sn]: !p[sn] }));

  async function markSelectedShipped() {
    setActionError(null);
    const settled = await Promise.allSettled(
      shippableRows.map((l) => licenseService.setOnboardingStatus(l.serialNumber, { shipped: true }, token)),
    );
    const failed = settled.filter((r) => r.status === 'rejected').length;
    if (failed) setActionError(`${failed} device(s) could not be marked shipped.`);
    setSelected({});
    await load();
  }

  return (
    <div className="customer-detail">
      <h1>{customerName} <span className="muted">({customerNumber})</span></h1>
      <p>{licenses.length} device(s)</p>

      <FleetStatusStrip counts={counts} activeStage={activeStage} onSelectStage={setActiveStage} />

      {actionError && <p className="batch-error" role="alert">{actionError}</p>}
      <div className="batch-action-bar">
        <Button variant="primary" disabled={selectedRows.length === 0} onClick={() => setShowProvision(true)}>
          Generate activation labels ({selectedRows.length})
        </Button>
        <Button variant="ghost" disabled={shippableRows.length === 0} onClick={markSelectedShipped}>
          Mark shipped ({shippableRows.length})
        </Button>
        {selectedRows.length > shippableRows.length && (
          <span className="batch-hint">Not-provisioned devices can't ship — generate a label first.</span>
        )}
      </div>

      <table className="device-table">
        <thead><tr><th scope="col"><span className="sr-only">Select</span></th><th scope="col">Serial</th><th scope="col">Stage</th><th scope="col">Printed</th><th scope="col">Shipped</th></tr></thead>
        <tbody>
          {visible.map((l) => (
            <tr key={l.serialNumber}>
              <td><input type="checkbox" aria-label={`Select ${l.serialNumber}`} checked={!!selected[l.serialNumber]} onChange={() => toggle(l.serialNumber)} /></td>
              <td>{l.serialNumber}</td>
              <td>
                <Badge variant={stageBadgeVariant(l.onboardingStage)}>{stageLabel(l.onboardingStage)}</Badge>
                {(l.overlays || []).map((o) => <Badge key={o} variant={o === 'expired' ? 'warning' : 'inactive'}>{o}</Badge>)}
              </td>
              <td>{l.labelPrintedAt ? '✓' : '—'}</td>
              <td>{l.shippedAt ? '✓' : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {showProvision && (
        <ActivationLabelExport
          licenses={selectedRows.map(({ licenseKey, licenseKeyHash, ...safe }) => safe)}
          onClose={() => { setShowProvision(false); load(); }}
        />
      )}
    </div>
  );
}
